import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type PopoverVisibilityPredicate = () => boolean | Promise<boolean>

export interface UsePopoverStateResult {
  isOpen: boolean
  close: () => Promise<boolean>
  deferClose: (predicate?: PopoverVisibilityPredicate) => Promise<boolean>
  deferOpen: (predicate?: PopoverVisibilityPredicate) => Promise<boolean>
  open: () => Promise<boolean>
  toggle: () => Promise<boolean>
}

/**
 * Tracks popover visibility and defers visibility requests until the next
 * render.
 *
 * `open`, `close`, and `toggle` change visibility right away.
 * `deferOpen` and `deferClose` wait for the next render so callers can finish
 * local state updates before the popover changes.
 */
export function usePopoverState(initialState = false): UsePopoverStateResult {
  const [isOpen, setIsOpen] = useState(initialState)
  const [pendingIsOpen, setPendingIsOpen] = useState<boolean | null>(null)

  // Newer requests bump the sequence so older async predicates do not apply later.
  const requestSequenceRef = useRef(0)

  // This ref keeps the next visible state available across rapid toggle calls.
  const requestedOpenRef = useRef(initialState)

  const setVisibilityState = useCallback((nextIsOpen: boolean) => {
    requestSequenceRef.current += 1
    requestedOpenRef.current = nextIsOpen
    setIsOpen(nextIsOpen)
  }, [])

  const requestVisibilityState = useCallback(
    async (nextIsOpen: boolean, predicate?: PopoverVisibilityPredicate) => {
      const requestSequence = ++requestSequenceRef.current

      if (predicate && !(await predicate())) return false
      // Only the latest deferred request may queue a visibility change.
      if (requestSequence !== requestSequenceRef.current) return false

      requestedOpenRef.current = nextIsOpen
      setPendingIsOpen(nextIsOpen)

      return true
    },
    []
  )

  const close = useCallback(async () => {
    setPendingIsOpen(null)
    setVisibilityState(false)
    return true
  }, [setVisibilityState])

  const open = useCallback(async () => {
    setPendingIsOpen(null)
    setVisibilityState(true)
    return true
  }, [setVisibilityState])

  const toggle = useCallback(async () => {
    setPendingIsOpen(null)

    // Toggle uses the last requested state so back-to-back calls alternate.
    const nextIsOpen = !requestedOpenRef.current

    setVisibilityState(nextIsOpen)
    return true
  }, [setVisibilityState])

  const deferClose = useCallback(
    (predicate?: PopoverVisibilityPredicate) => {
      return requestVisibilityState(false, predicate)
    },
    [requestVisibilityState]
  )

  const deferOpen = useCallback(
    (predicate?: PopoverVisibilityPredicate) => {
      return requestVisibilityState(true, predicate)
    },
    [requestVisibilityState]
  )

  useEffect(() => {
    if (pendingIsOpen === null) return

    // The pending open state applies after React has already rendered local updates.
    setVisibilityState(pendingIsOpen)
    setPendingIsOpen(null)
  }, [pendingIsOpen, setVisibilityState])

  return useMemo(
    () => ({
      isOpen,
      close,
      deferClose,
      deferOpen,
      open,
      toggle
    }),
    [close, deferClose, deferOpen, isOpen, open, toggle]
  )
}
